import { ChevronLeft, Close } from '@casumo/dojo-react-icons';
import classNames from 'classnames';
import { FC, memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import ButtonRenderer from './buttonRenderer.tsx';
import StepComponent from './stepComponent.tsx';
import { CasumoIcon, CasumoLoaderIcon } from '../assets/icons';
import { useFlowContext } from '../hooks/useFlowContext.tsx';
import useFlowNavigation from '../hooks/useFlowNavigation.tsx';
import useMarketInfo from '../hooks/useMarketInfo.tsx';
import useNavigateTo from '../hooks/useNavigateTo.tsx';
import useShouldHidePnpClose from '../hooks/useShouldHidePnpClose.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TEST_ID } from '../utils/constants.ts';
import { URL } from '../utils/envConstants.ts';
import { colorToRgba, resetProgressInLocalStorage } from '../utils/utils.ts';

/**
 * A component used to render the registration dialog and its consistent blocks.
 */
const Flow: FC = () => {
  const { config } = useFlowContext();
  const marketInfo = useMarketInfo();
  const {
    step,
    handleNext,
    handlePrevious,
  } = useFlowNavigation(marketInfo, config);
  const { setMetadata } = useRegistrationStore();
  const { navigateToMarket } = useNavigateTo();
  const hideClose = useShouldHidePnpClose();

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [gradientStyle, setGradientStyle] = useState('');

  const currentStepConfig = config.steps?.[step];

  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const checkVerticalScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (footerRef.current) {
        const fixedDivHeight = footerRef.current.offsetHeight;
        document.documentElement.style.setProperty(
          '--fixed-div-height',
          `${fixedDivHeight}px`,
        );
      }

      if (contentRef.current) {
        setIsOverflowing(
          contentRef.current.scrollHeight > contentRef.current.clientHeight,
        );
      }
    });

    const fromColor = colorToRgba(
      currentStepConfig?.overflowingColor || currentStepConfig?.backgroundColor || 'rgba(239, 242, 244, 0.00)',
      0,
    );
    const midColor = colorToRgba(
      currentStepConfig?.overflowingColor || currentStepConfig?.backgroundColor || 'rgba(239, 242, 244, 0.90)',
      0.9,
    );
    const toColor = colorToRgba(
      currentStepConfig?.overflowingColor || currentStepConfig?.backgroundColor || 'rgba(239, 242, 244, 1)',
      1,
    );

    setGradientStyle(
      `linear-gradient(180deg, ${fromColor} 0%, ${midColor} 37%, ${toColor} 100%)`,
    );
  }, [currentStepConfig]);

  useEffect(() => {
    setMetadata({ currentStep: currentStepConfig?.stepName });
  }, [currentStepConfig?.stepName, setMetadata]);

  useEffect(() => {
    checkVerticalScroll();

    window.addEventListener('resize', checkVerticalScroll);
    window.visualViewport?.addEventListener('resize', checkVerticalScroll);

    return () => {
      window.removeEventListener('resize', checkVerticalScroll);
      window.visualViewport?.removeEventListener('resize', checkVerticalScroll);
    };
  }, [currentStepConfig, checkVerticalScroll]);

  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver(() => {
      checkVerticalScroll();
    });

    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
    };
  }, [checkVerticalScroll]);

  const onClose = () => {
    resetProgressInLocalStorage(marketInfo.market);
    setMetadata({ isOpen: false });
    sessionStorage.setItem('fabric:registration:open', 'false');
    navigateToMarket(marketInfo.market);
  };

  const Loader = (
    <div className="animate-spin rounded-md">
      <CasumoLoaderIcon width={64} height={64} />
    </div>
  );

  return (
    <Suspense fallback={Loader}>
      <div
        className="flex flex-col h-full w-full"
        style={{
          backgroundColor: currentStepConfig?.backgroundColor ?? 'white',
        }}
      >
        <div
          className="flex items-center justify-between w-full px-2xs md:px-md h-[62px] md:h-[72px] overflow-hidden"
          style={{ backgroundColor: currentStepConfig?.backgroundColor ?? 'white' }}
        >
          <button
            onClick={handlePrevious}
            className={classNames(
              'flex items-center justify-start md:justify-center h-fit w-2xl rounded-sm',
              'text-[24px] focus:outline-purpleblue',
              { invisible: step === 1 },
            )}
            style={{
              color: currentStepConfig?.headerTextColor ?? '#4C00C2',
            }}
            data-testid={TEST_ID.PREFIX + 'back-button'}
          >
            <ChevronLeft />
          </button>
          <div
            className="items-center justify-center gap-2xs w-full hidden sm:flex"
            style={{
              color: currentStepConfig?.headerTextColor ?? 'black',
            }}
            data-testid={TEST_ID.PREFIX + 'header'}
          >
            {currentStepConfig?.headerIcon && (
              <img
                src={currentStepConfig?.headerIcon}
                className="w-md h-md object-cover"
              />
            )}
            <span className={classNames({
              'font-extrabold text-[20px]': !currentStepConfig?.headerIcon,
              'text-base':                  currentStepConfig?.headerIcon,
            })}
            data-testid={TEST_ID.PREFIX + 'header-text'}
            >
              {currentStepConfig?.name}
            </span>
          </div>
          <a className="sm:hidden" href={`${URL}/${marketInfo?.market}`}>
            <CasumoIcon color={currentStepConfig?.headerTextColor ?? '#4C00C2'} />
          </a>
          {hideClose ? (
            <div className="w-2xl" />
          ) : (
            <div
              className="flex items-center justify-end text-[24px] mr-3xs w-2xl h-full md:invisible"
              onClick={onClose}
              style={{
                color: currentStepConfig?.headerTextColor ?? '#3C0098',
              }}
              data-testid={TEST_ID.BUTTON_CLOSE}
            >
              <Close style={{
                display: window?.native ? 'none' : 'flex',
              }} />
            </div>
          )}
        </div>
        <div
          className={classNames(`peer flex flex-col gap-xs h-full w-full bg-grey-0
              justify-${currentStepConfig?.title ? 'around' : 'start'}
              p-sm md:px-2xl lg:px-2xl md:pt-md lg:pt-lg overflow-y-auto animate-fadeIn`)}
          style={{
            paddingBottom:      'var(--fixed-div-height)',
            backgroundColor:    currentStepConfig?.backgroundColor,
            backgroundImage:    `url(${currentStepConfig?.backgroundImage})`,
            backgroundRepeat:   'no-repeat',
            backgroundPosition: 'center',
            backgroundSize:     'cover',
          }}
          ref={contentRef}
        >
          {currentStepConfig?.title ? (
            <main key={currentStepConfig.name}
              className="flex flex-col h-full items-center justify-center gap-7 pd:mt-lg animate-fadeIn">
              <div
                className="text-center text-purpleblue font-midnight-st text-[90px]
                      font-black uppercase tracking-normal leading-[70px] max-w-[580px]"
                dangerouslySetInnerHTML={{ __html: currentStepConfig?.title }}
                data-testid={TEST_ID.TITLE + currentStepConfig.stepName}
              />
              <p
                className="text-grey-90 text-center text-base max-w-[460px] font-circular-pro"
                data-testid={TEST_ID.DESCRIPTION + currentStepConfig.stepName}
              >
                {currentStepConfig?.description}
              </p>
              <p
                className="text-grey-90 text-center text-body-base max-w-[460px] font-circular-pro"
                data-testid={TEST_ID.PREFIX + 'details'}
              >
                {currentStepConfig?.details}
              </p>
            </main>
          ) : (
            <StepComponent step={currentStepConfig} />
          )}
        </div>
        <div
          className={classNames(
            'fixed bottom-0 w-full py-md px-sm',
            'md:px-2xl pt-[50px] animate-fadeIn pointer-events-none',
          )}
          style={{ background: isOverflowing ? gradientStyle : 'transparent' }}
          ref={footerRef}
        >
          <ButtonRenderer
            currentStepConfig={currentStepConfig}
            handleNext={handleNext}
          />
        </div>
      </div>
    </Suspense>
  );
};

const Memoized = memo(Flow);
Memoized.displayName = 'Flow';

export default Memoized;
